import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, XCircle, Loader2, Flame } from "lucide-react";

export default function FuelSuccess() {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = sp.get("session_id");
    const [state, setState] = useState({ status: "checking", data: null });

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", data: null });
            return;
        }
        let attempts = 0;
        const poll = async () => {
            attempts++;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "success", data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", data });
                    return;
                }
            } catch {
                if (attempts >= 6) {
                    setState({ status: "error", data: null });
                    return;
                }
            }
            if (attempts >= 8) {
                setState({ status: "timeout", data: null });
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-background grain-overlay flex items-center justify-center p-6">
            <div className="glass rounded-2xl p-10 max-w-lg w-full text-center">
                {state.status === "checking" && (
                    <>
                        <Loader2 className="mx-auto animate-spin text-[hsl(45_85%_68%)] mb-4" size={48} />
                        <div className="font-heading text-2xl">Igniting payload...</div>
                        <div className="text-sm text-muted-foreground mt-2">Routing ad-capital to the Foundry Ad Treasury.</div>
                    </>
                )}
                {state.status === "success" && (
                    <>
                        <div className="w-16 h-16 mx-auto rounded-full bg-[hsl(40_61%_25%)] flex items-center justify-center text-[hsl(45_85%_68%)] mb-4">
                            <Flame size={28} />
                        </div>
                        <div className="font-heading text-3xl gold-gradient-text">Strike active</div>
                        <div className="text-sm text-muted-foreground mt-2">
                            ${state.data?.amount_total ? (state.data.amount_total / 100).toLocaleString() : "—"} deployed to {state.data?.state_code || "—"} · 30-day burn
                        </div>
                        <div className="mt-6 p-4 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.2)] text-left text-xs space-y-2">
                            <Row label="Site" value={state.data?.state_code} mono />
                            <Row label="Payload" value={`$${state.data?.amount_total ? (state.data.amount_total / 100).toLocaleString() : "—"}`} />
                            <Row label="Status" value="active · 30 days" />
                        </div>
                        <button
                            onClick={() => navigate("/app/dashboard")}
                            data-testid="fuel-success-dashboard"
                            className="mt-6 w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        >
                            Return to Transparency
                        </button>
                    </>
                )}
                {(state.status === "error" || state.status === "expired" || state.status === "timeout") && (
                    <>
                        <XCircle className="mx-auto text-[hsl(350_75%_60%)] mb-4" size={56} />
                        <div className="font-heading text-2xl">Ingress incomplete</div>
                        <div className="text-sm text-muted-foreground mt-2">
                            {state.status === "expired" ? "Checkout session expired." : "We couldn't confirm the payload."}
                        </div>
                        <button onClick={() => navigate("/app/dashboard")} className="mt-6 px-6 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors">
                            Back to Dashboard
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground uppercase tracking-wider">{label}</span>
            <span className={mono ? "font-mono" : ""}>{value}</span>
        </div>
    );
}
